import React, { useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  FileSpreadsheet,
  Loader2,
  Upload,
  X,
  XCircle,
  Sparkles,
  Package,
  BookOpen,
} from 'lucide-react';
import { cn } from '../../../ui/utils';
import type { ImportResult } from '../services/MenuExcelEngine';

export type ImportStrategy = 'replace' | 'append';

export type MenuImportStage = 'reading' | 'analyzing' | 'mapping' | 'applying' | 'done' | 'error';

export interface MenuImportState {
  fileName: string;
  stage: MenuImportStage;
  progress: number;
  report?: ImportResult;
  error?: string;
}

// ─── Inline Slide-down Excel Toolbar ────────────────────────────────────────

interface ExcelToolbarProps {
  isOpen: boolean;
  status: MenuImportState | null;
  onClose: () => void;
  onProcessFile: (file: File, strategy: ImportStrategy) => void;
  onDownloadTemplate: () => void;
  onExportCurrent?: () => void;
  onRecipeBook?: () => void;
}

const STAGE_LABELS: Record<MenuImportStage, string> = {
  reading: 'Reading workbook…',
  analyzing: 'Detecting format…',
  mapping: 'Mapping menu data…',
  applying: 'Applying changes…',
  done: 'Import complete',
  error: 'Import failed',
};

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const ExcelToolbar: React.FC<ExcelToolbarProps> = ({
  isOpen,
  status,
  onClose,
  onProcessFile,
  onDownloadTemplate,
  onExportCurrent,
  onRecipeBook,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [strategy, setStrategy] = useState<ImportStrategy>('replace');

  const busy = !!status && !['done', 'error'].includes(status.stage);

  const summary = useMemo(() => {
    if (!status?.report) return [];
    return [
      { label: 'Cat', value: status.report.categoriesCreated + status.report.categoriesUpdated },
      { label: 'Items', value: status.report.itemsCreated + status.report.itemsUpdated },
      { label: 'Groups', value: status.report.groupsCreated },
      { label: 'Options', value: status.report.optionsCreated },
    ];
  }, [status]);

  const triggerFilePicker = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setSelectedFile(f);
    e.target.value = '';
  };

  const handleProcess = () => {
    if (!selectedFile) return;
    onProcessFile(selectedFile, strategy);
  };

  const handleClose = () => {
    if (busy) return;
    setSelectedFile(null);
    onClose();
  };

  return (
    <div className={cn(
      'overflow-hidden transition-all duration-300 ease-in-out',
      isOpen ? 'max-h-[200px] opacity-100' : 'max-h-0 opacity-0',
    )}>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        {/* Accent top bar */}
        <div className="h-0.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-emerald-500" />

        {/* Hidden file input */}
        <input ref={inputRef} type="file" accept=".xlsx,.xls,.zip" className="hidden" onChange={handleFileChange} />

        {/* ── Main toolbar row ── */}
        <div className="px-4 py-3 flex flex-wrap items-center gap-3">

          {/* Group 1: Export */}
          <div className="flex items-center gap-2 border-r border-slate-200 pr-3">
            <button onClick={onExportCurrent} disabled={!onExportCurrent || busy}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-xs font-bold text-white hover:from-indigo-700 hover:to-purple-700 transition shadow-sm disabled:opacity-40 disabled:cursor-not-allowed">
              <Sparkles size={13} /> Export Menu
            </button>
            {onRecipeBook && (
              <button onClick={onRecipeBook} disabled={busy}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-xs font-bold text-white hover:from-amber-600 hover:to-orange-600 transition shadow-sm disabled:opacity-40 disabled:cursor-not-allowed">
                <BookOpen size={13} /> Recipe Book
              </button>
            )}
          </div>

          {/* Group 2: Import status / File selector */}
          <div className="flex items-center gap-2 border-r border-slate-200 pr-3">
            {/* Status line (during/after import) */}
            {status && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-slate-50 text-xs">
                {busy && <Loader2 size={12} className="animate-spin text-indigo-500" />}
                {status.stage === 'done' && <CheckCircle2 size={12} className="text-emerald-500" />}
                {status.stage === 'error' && <AlertTriangle size={12} className="text-rose-500" />}
                <span className="font-medium text-slate-600">{STAGE_LABELS[status.stage]}</span>
              </div>
            )}

            {/* Selected file indicator */}
            {selectedFile && !status && (() => {
              const isZip = selectedFile.name.toLowerCase().endsWith('.zip');
              return (
              <div className={cn('flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs',
                isZip ? 'border-orange-200 bg-orange-50' : 'border-emerald-200 bg-emerald-50')}>
                {isZip ? <Package size={12} className="text-orange-500" /> : <CheckCircle2 size={12} className="text-emerald-500" />}
                <span className={cn('font-semibold max-w-[160px] truncate', isZip ? 'text-orange-700' : 'text-emerald-700')}>{selectedFile.name}</span>
                <span className={isZip ? 'text-orange-400' : 'text-emerald-400'}>{formatFileSize(selectedFile.size)}</span>
                {isZip && <span className="text-[10px] text-orange-500 font-medium">Bulk Package</span>}
                <button onClick={() => setSelectedFile(null)} className={cn('p-0.5 rounded transition', isZip ? 'hover:bg-orange-100' : 'hover:bg-emerald-100')}>
                  <XCircle size={12} className={isZip ? 'text-orange-400' : 'text-emerald-400'} />
                </button>
              </div>
              );
            })()}

            {/* New / Change File button — always triggers file picker */}
            {!busy && (
              <button onClick={triggerFilePicker}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-slate-300 bg-white text-xs font-bold text-slate-600 hover:border-indigo-300 hover:bg-indigo-50 transition">
                <Upload size={13} /> {selectedFile || (status?.stage === 'done') ? 'Change File' : 'New'}
              </button>
            )}

            {/* Process button — only when a file is selected and no import running */}
            {selectedFile && !status && (
              <button onClick={handleProcess}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-indigo-600 text-xs font-bold text-white hover:bg-indigo-700 transition shadow-sm">
                🚀 Process & Import
              </button>
            )}
          </div>

          {/* Group 3: Strategy toggles */}
          <div className="flex items-center gap-1 bg-slate-50 rounded-lg p-1 border border-slate-200">
            <button
              type="button" onClick={() => setStrategy('replace')} disabled={busy}
              className={cn(
                'px-3 py-1.5 rounded-md text-[11px] font-bold transition',
                strategy === 'replace'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-white',
              )}
            >Replace</button>
            <button
              type="button" onClick={() => setStrategy('append')} disabled={busy}
              className={cn(
                'px-3 py-1.5 rounded-md text-[11px] font-bold transition',
                strategy === 'append'
                  ? 'bg-emerald-600 text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-white',
              )}
            >Append / Update</button>
          </div>

          {/* Close / Collapse */}
          <button onClick={handleClose} disabled={busy}
            className="ml-auto p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition disabled:opacity-40"
            title="Close Excel Tools">
            <X size={16} />
          </button>
        </div>

        {/* ── Progress bar (only during processing) ── */}
        {status && (
          <div className="h-1 bg-slate-200">
            <div className={cn('h-full transition-all duration-500',
              status.stage === 'error' ? 'bg-rose-500' : 'bg-[linear-gradient(90deg,#4f46e5,#10b981)]'
            )} style={{ width: `${Math.min(Math.max(status.progress, 0), 100)}%` }} />
          </div>
        )}

        {/* ── Result summary (only after import completes) ── */}
        {status?.report && (status.stage === 'done' || status.stage === 'error') && (
          <div className="px-4 py-2 border-t border-slate-200 bg-white flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              {status.report.errors.length > 0
                ? <><AlertTriangle size={12} className="text-amber-500" /><span className="font-bold text-amber-700">{status.report.errors.length} warning(s)</span></>
                : <><CheckCircle2 size={12} className="text-emerald-500" /><span className="font-bold text-emerald-700">Clean import</span></>
              }
            </div>
            <div className="flex items-center gap-3 text-slate-500">
              {summary.map(s => (
                <span key={s.label}><span className="font-bold text-slate-700">{s.value}</span> {s.label}</span>
              ))}
            </div>
            {status.report.errors.length > 0 && (
              <span className="text-amber-600 truncate max-w-[200px]" title={status.report.errors[0]}>{status.report.errors[0]}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Keep re-export for backwards compat (used by BuilderDrawer fallback)
export const ImportExcelDialog = ExcelToolbar;